// MapView — zoom bar (+/- with px readout + glyph legend + one "Location
// activities" jump) over the canvas. Draws vsj.map: biome squares with fog
// (visited full, sight ring, seen dimmed biome-only, unknown = faint
// --map-unknown squares continuing the grid),
// node glyphs, badges, names at high zoom or hover, player marker, guidance
// tile pulse, travel path + moving token, arrival flash. Scroll = zoom,
// drag = pan, click = select_tile into the info pane.

import { artImageURL } from '../icons';
import { el, tooltip } from '../kit';
import { sendEvent, serverNow } from '../net';
import * as ui from '../ui';
import * as vsj from '../vsj';
import * as sfx from '../audio/sfx';
import { onFxType } from '../fx';

const TILE_MIN = 20, TILE_MAX = 110, NAME_ZOOM = 64, ZOOM_STEP = 1.15;
const ARRIVE_FLASH_S = 1.4;
const HOVER_LIFT_PX = 2;              // hovered tile rises this much

const BIOME: Record<string, string> = {
  meadows: '#8fae57', forest: '#3f6d3a', mountain: '#8b91a1',
  swamp: '#5a6b4a', jungle: '#2e8b57',
};
const BIOME_UNKNOWN = '#24201b';

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

const glyphCache = new Map<string, HTMLImageElement>();
function glyphImg(key: string): HTMLImageElement {
  let img = glyphCache.get(key);
  if (!img) {
    img = new Image();
    void artImageURL(key).then((url) => { if (url) img!.src = url; });
    glyphCache.set(key, img);
  }
  return img;
}

export function mapView(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const canvas = el('canvas', { style: 'touch-action:none' });
  const wrap = el('div', { class: 'map-wrap' }, canvas);
  const zoomLabel = el('span', { class: 'k-small k-dim', style: 'min-width:56px;text-align:center' });
  let tilePx = 56;
  let center = { x: 0, y: 0 };
  let centeredOnce = false;
  let hoverTile: { x: number; y: number } | null = null;
  let dragging = false, dragMoved = false;
  let arrive: { x: number; y: number; name: string; t0: number } | null = null;

  const setZoom = (px: number) => {
    tilePx = Math.max(TILE_MIN, Math.min(TILE_MAX, px));
    zoomLabel.textContent = `${Math.round(tilePx)} px`;
  };
  setZoom(56);

  const minus = el('button', { class: 'k-btn', text: '−', onclick: () => setZoom(tilePx / ZOOM_STEP) });
  const plus = el('button', { class: 'k-btn', text: '+', onclick: () => setZoom(tilePx * ZOOM_STEP) });
  tooltip(minus, () => 'Zoom out');
  tooltip(plus, () => 'Zoom in');
  const legend = el('div', { style: 'display:flex;gap:10px;align-items:center;flex:1;justify-content:flex-end' });
  for (const [g, word] of [['glyph_vein', 'vein'], ['glyph_stand', 'stand'], ['glyph_fish', 'fishing'], ['glyph_creature', 'creatures'], ['glyph_npc', 'someone']]) {
    const img = el('img', { style: 'width:16px;height:16px' }) as HTMLImageElement;
    void artImageURL(g).then((url) => { if (url) img.src = url; });
    legend.append(el('span', { style: 'display:inline-flex;gap:3px;align-items:center' }, img,
      el('span', { class: 'k-small k-faint', text: word })));
  }
  legend.append(el('span', { class: 'k-small k-faint', style: 'opacity:0.7', text: 'sight grows with Spotting' }));
  const locBtn = el('button', { class: 'k-btn', text: 'Location activities ›' });
  tooltip(locBtn, () => 'Open the Location tab — everything you can do where you stand');
  locBtn.onclick = () => ui.requestView('location');

  const root = el(
    'div',
    { class: 'k-panel', style: 'flex:1;min-height:0;display:flex;flex-direction:column;padding:6px;gap:6px' },
    el('div', { class: 'map-toolbar' }, minus, zoomLabel, plus, legend, locBtn),
    wrap,
  );

  const map = (): any => vsj.at('map') ?? {};
  const travel = (): any => {
    const tr = vsj.at('current_activity.travel');
    return tr && typeof tr === 'object' ? tr : null;
  };
  const pulseTile = (): { x: number; y: number } | null => {
    for (const g of (vsj.at('guidance.glow') ?? []) as string[]) {
      if (typeof g === 'string' && g.startsWith('tile:')) {
        const [x, y] = g.slice(5).split(',').map(Number);
        if (!Number.isNaN(x) && !Number.isNaN(y)) return { x, y };
      }
    }
    return null;
  };

  const centerOnPlayer = () => {
    const p = map().player;
    if (p && typeof p === 'object') {
      center = { x: Number(p.x ?? 0), y: Number(p.y ?? 0) };
      centeredOnce = true;
    }
  };

  const w2s = (wx: number, wy: number, W: number, H: number) => ({
    x: W / 2 + (wx - center.x) * tilePx,
    y: H / 2 + (wy - center.y) * tilePx,
  });
  const s2t = (sx: number, sy: number) => {
    const r = canvas.getBoundingClientRect();
    return {
      x: Math.round((sx - r.width / 2) / tilePx + center.x),
      y: Math.round((sy - r.height / 2) / tilePx + center.y),
    };
  };

  onFxType('arrived', (f: any) => {
    arrive = { x: Number(f.x ?? 0), y: Number(f.y ?? 0), name: String(f.name ?? ''), t0: performance.now() };
  });

  const draw = () => {
    requestAnimationFrame(draw);
    if (root.offsetParent === null) return;
    const r = wrap.getBoundingClientRect();
    if (
      canvas.width !== Math.round(r.width * devicePixelRatio) ||
      canvas.height !== Math.round(r.height * devicePixelRatio)
    ) {
      canvas.width = Math.round(r.width * devicePixelRatio);
      canvas.height = Math.round(r.height * devicePixelRatio);
    }
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    const W = r.width, H = r.height;
    ctx.clearRect(0, 0, W, H);
    if (!centeredOnce) centerOnPlayer();

    const m = map();
    const tiles = m.tiles ?? {};
    const bg = cssVar('--bg');
    const half = tilePx / 2;

    // the void reads as bg, and the empty grid continues into it: every
    // viewport cell gets a square just off the background (--map-unknown);
    // known tiles overdraw theirs
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = cssVar('--map-unknown');
    const wx0 = Math.floor(center.x - W / (2 * tilePx)) - 1;
    const wx1 = Math.ceil(center.x + W / (2 * tilePx)) + 1;
    const wy0 = Math.floor(center.y - H / (2 * tilePx)) - 1;
    const wy1 = Math.ceil(center.y + H / (2 * tilePx)) + 1;
    for (let wy = wy0; wy <= wy1; wy++)
      for (let wx = wx0; wx <= wx1; wx++) {
        const uc = w2s(wx, wy, W, H);
        ctx.fillRect(uc.x - half + 1, uc.y - half + 1, tilePx - 2, tilePx - 2);
      }

    for (const k of Object.keys(tiles)) {
      const t = tiles[k];
      if (!t || typeof t !== 'object') continue;
      const [tx, ty] = k.split(',').map(Number);
      const c = w2s(tx, ty, W, H);
      const x0 = c.x - half, y0 = c.y - half;
      if (x0 > W || y0 > H || x0 + tilePx < 0 || y0 + tilePx < 0) continue;
      const fog = String(t.fog ?? 'seen');
      let col = BIOME[String(t.biome ?? '')] ?? BIOME_UNKNOWN;
      if (fog === 'seen') {
        // dimmed memory: blend toward bg
        ctx.globalAlpha = 1;
        ctx.fillStyle = bg;
        ctx.fillRect(x0 + 1, y0 + 1, tilePx - 2, tilePx - 2);
        ctx.globalAlpha = 0.45;
      }
      ctx.fillStyle = col;
      ctx.fillRect(x0 + 1, y0 + 1, tilePx - 2, tilePx - 2);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = 'rgba(0,0,0,0.35)';
      ctx.strokeRect(x0 + 1, y0 + 1, tilePx - 2, tilePx - 2);
      if (fog === 'sight') {
        ctx.strokeStyle = 'rgba(235,227,204,0.35)';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x0 + 3, y0 + 3, tilePx - 6, tilePx - 6);
        ctx.lineWidth = 1;
      }
      const glyphs = t.glyphs;
      if (Array.isArray(glyphs) && glyphs.length && tilePx >= 30) {
        const gs = Math.max(10, Math.min(26, tilePx * 0.3));
        let gx = x0 + 3;
        for (const g of glyphs) {
          const img = glyphImg('glyph_' + g);
          if (img.complete && img.naturalWidth) ctx.drawImage(img, gx, y0 + tilePx - gs - 3, gs, gs);
          gx += gs + 2;
        }
      }
      const badge = t.badge;
      if (badge != null && Number(badge) > 0) {
        const br = Math.max(7, Math.min(13, tilePx * 0.16));
        const bx = x0 + tilePx - br - 2, by = y0 + br + 2;
        ctx.fillStyle = cssVar('--bg-modal');
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = cssVar('--border-light');
        ctx.stroke();
        ctx.fillStyle = cssVar('--text');
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(Number(badge)), bx, by + 4);
        ctx.textAlign = 'left';
      }
      if (t.name != null && (tilePx >= NAME_ZOOM || (hoverTile && hoverTile.x === tx && hoverTile.y === ty))) {
        const nm = String(t.name);
        ctx.font = '11px sans-serif';
        const tw = ctx.measureText(nm).width;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(c.x - tw / 2 - 2, y0 + 2, tw + 4, 14);
        ctx.fillStyle = cssVar('--text');
        ctx.fillText(nm, c.x - tw / 2, y0 + 13);
      }
    }

    // hovered tile lifts to meet the pointer (living-map hover)
    const reduced = ui.reducedMotion();
    if (hoverTile && !dragging && !reduced) {
      const ht = tiles[`${hoverTile.x},${hoverTile.y}`];
      if (ht && typeof ht === 'object') {
        const c = w2s(hoverTile.x, hoverTile.y, W, H);
        const x0 = c.x - half, y0 = c.y - half - HOVER_LIFT_PX;
        const col = BIOME[String(ht.biome ?? '')] ?? BIOME_UNKNOWN;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = String(ht.fog ?? 'seen') === 'seen' ? bg : col;
        ctx.fillRect(x0 + 1, y0 + 1, tilePx - 2, tilePx - 2);
        ctx.restore();
        if (String(ht.fog ?? 'seen') === 'seen') {
          ctx.globalAlpha = 0.45;
          ctx.fillStyle = col;
          ctx.fillRect(x0 + 1, y0 + 1, tilePx - 2, tilePx - 2);
          ctx.globalAlpha = 1;
        }
        ctx.strokeStyle = cssVar('--border-light');
        ctx.strokeRect(x0 + 1, y0 + 1, tilePx - 2, tilePx - 2);
      }
    }

    // guidance pulse
    const pulse = pulseTile();
    if (pulse) {
      const c = w2s(pulse.x, pulse.y, W, H);
      const a = Math.max(0, Math.min(1, 0.45 + 0.45 * Math.sin(performance.now() / 200)));
      ctx.strokeStyle = `rgba(232,145,45,${a})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(c.x - half + 2, c.y - half + 2, tilePx - 4, tilePx - 4);
      ctx.lineWidth = 1;
    }

    // travel path + token
    const player = m.player ?? {};
    const px = Number(player.x ?? 0), py = Number(player.y ?? 0);
    const tr = travel();
    if (tr) {
      const parts = String(tr.dest ?? '').split(',').map(Number);
      if (parts.length === 2 && !parts.some(Number.isNaN)) {
        const from = w2s(px, py, W, H);
        const to = w2s(parts[0], parts[1], W, H);
        const path = cssVar('--bar-fill');
        ctx.strokeStyle = path;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
        ctx.lineWidth = 1;
        const depart = Number(tr.depart_at ?? 0), arriveAt = Number(tr.arrive_at ?? 0);
        if (arriveAt > depart) {
          const frac = Math.max(0, Math.min(1, (serverNow() - depart) / (arriveAt - depart)));
          const trr = Math.max(4, Math.min(9, tilePx * 0.14));
          const tok = { x: from.x + (to.x - from.x) * frac, y: from.y + (to.y - from.y) * frac };
          ctx.fillStyle = bg;
          ctx.beginPath();
          ctx.arc(tok.x, tok.y, trr + 1.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = path;
          ctx.beginPath();
          ctx.arc(tok.x, tok.y, trr, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // arrival flash
    if (arrive) {
      const age = (performance.now() - arrive.t0) / 1000;
      if (age > ARRIVE_FLASH_S) arrive = null;
      else {
        const p = age / ARRIVE_FLASH_S;
        const c = w2s(arrive.x, arrive.y, W, H);
        const grow = 2 + p * tilePx * 0.35;
        ctx.strokeStyle = `rgba(242,230,194,${(1 - p) * 0.9})`;
        ctx.lineWidth = 3;
        ctx.strokeRect(c.x - half - grow, c.y - half - grow, tilePx + grow * 2, tilePx + grow * 2);
        ctx.lineWidth = 1;
        if (arrive.name) {
          ctx.font = '14px sans-serif';
          const tw = ctx.measureText(arrive.name).width;
          ctx.fillStyle = `rgba(0,0,0,${Math.min(0.85, (1 - p) * 1.4)})`;
          ctx.fillRect(c.x - tw / 2 - 3, c.y - half - 24, tw + 6, 18);
          ctx.fillStyle = cssVar('--text');
          ctx.globalAlpha = Math.min(1, (1 - p) * 1.6);
          ctx.fillText(arrive.name, c.x - tw / 2, c.y - half - 10);
          ctx.globalAlpha = 1;
        }
      }
    }

    // player marker
    const pp = w2s(px, py, W, H);
    const pr = Math.max(5, Math.min(12, tilePx * 0.18));
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.arc(pp.x, pp.y, pr + 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = cssVar('--text');
    ctx.beginPath();
    ctx.arc(pp.x, pp.y, pr, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = cssVar('--border');
    ctx.stroke();
  };
  requestAnimationFrame(draw);

  // fog-aware hover tooltip
  const tipText = (t: any): string => {
    if (!t || typeof t !== 'object') return '';
    const fog = String(t.fog ?? 'seen');
    const biome = String(t.biome ?? '?');
    const biomeCap = biome[0]?.toUpperCase() + biome.slice(1);
    if (fog === 'seen') return `${biomeCap} — a hazy memory`;
    const lines: string[] = [];
    if (t.name != null) lines.push(String(t.name));
    let sub = biomeCap;
    if (t.tier != null) sub += ` · T${t.tier}`;
    lines.push(sub);
    const glyphs = t.glyphs;
    if (Array.isArray(glyphs) && glyphs.length)
      lines.push('Has: ' + glyphs.map((g: string) => (g === 'npc' ? 'someone' : g)).join(', '));
    if (t.badge != null && Number(t.badge) > 0)
      lines.push(`${t.badge} stack${Number(t.badge) === 1 ? '' : 's'} stored`);
    return lines.join('\n');
  };
  tooltip(canvas, () => {
    if (!hoverTile) return null;
    const t = (map().tiles ?? {})[`${hoverTile.x},${hoverTile.y}`];
    let text = tipText(t);
    if (!text) return null;
    // the selected tile's card may carry served beast-tier prose — show it
    // only where the tooltip already surfaces tier text
    const tc = vsj.at('tile_card');
    if (
      tc && typeof tc === 'object' && tc.tier_beasts != null &&
      Number(tc.x) === hoverTile.x && Number(tc.y) === hoverTile.y &&
      t && typeof t === 'object' && t.tier != null
    )
      text += `\n${String(tc.tier_beasts)}`;
    const p = map().player ?? {};
    if (hoverTile.x === Number(p.x) && hoverTile.y === Number(p.y)) text += '\nYou are here';
    const node = el('div', { style: 'white-space:pre-line', text });
    return node;
  });

  // input
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    setZoom(e.deltaY < 0 ? tilePx * ZOOM_STEP : tilePx / ZOOM_STEP);
  });
  // pan/tap/pinch track explicit per-pointer positions (movementX/Y is 0 for
  // touch pointers on several browsers); two pointers = pinch zoom
  const active = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  canvas.addEventListener('pointerdown', (e) => {
    active.set(e.pointerId, { x: e.clientX, y: e.clientY });
    canvas.setPointerCapture(e.pointerId);
    if (active.size === 1) {
      dragging = true;
      dragMoved = false;
    } else {
      dragMoved = true; // a pinch never taps
      pinchDist = 0;
    }
  });
  canvas.addEventListener('pointermove', (e) => {
    const r = canvas.getBoundingClientRect();
    hoverTile = s2t(e.clientX - r.left, e.clientY - r.top);
    const prev = active.get(e.pointerId);
    if (!prev) return; // buttonless mouse hover
    const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
    active.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (active.size >= 2) {
      const pts = [...active.values()];
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (pinchDist > 0 && d > 0) setZoom((tilePx * d) / pinchDist);
      pinchDist = d;
      return;
    }
    if (dragging && Math.abs(dx) + Math.abs(dy) > 1) {
      dragMoved = true;
      center.x -= dx / tilePx;
      center.y -= dy / tilePx;
    }
  });
  const endPointer = (e: PointerEvent, tap: boolean) => {
    const wasTap = tap && dragging && !dragMoved && active.size === 1;
    active.delete(e.pointerId);
    pinchDist = 0;
    if (wasTap) {
      const r = canvas.getBoundingClientRect();
      const t = s2t(e.clientX - r.left, e.clientY - r.top);
      sfx.playEvent('ui_tap'); // tiles have a tap voice of their own
      sendEvent('select_tile', { x: t.x, y: t.y });
      ui.select(`tile:${t.x},${t.y}`);
    }
    dragging = active.size > 0;
    if (dragging) dragMoved = true; // a finger leaving a pinch isn't a fresh tap
  };
  canvas.addEventListener('pointerup', (e) => endPointer(e, true));
  canvas.addEventListener('pointercancel', (e) => endPointer(e, false));
  canvas.addEventListener('pointerleave', () => (hoverTile = null));

  return {
    root,
    refresh() {
      /* canvas redraws every frame while visible */
    },
  };
}
